import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import {
  CompareFieldData,
  JobRequisition,
  OfferFull,
  OpeningAssignment,
  RequisitionStatus,
} from '@recruitee/http-types';
import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';

import { EntityStoreKey } from '../../types';
import { JobRequisitionsEntity } from './job-requisitions-store.types';

const {
  namespace,
  selectors: defaultSelectors,
  actions: defaultActions,
  reducer,
} = createEntityCollectionStore<JobRequisitionsEntity>()(EntityStoreKey.JobRequisitions, {
  idSelector: e => e.offerId,
  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => ({
    fetchOne: (state, payload: { offerId: number }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.offerId] }),
    fetchOneSuccess: (state, payload: { scope: null; data: JobRequisitionsEntity }) =>
      pipeState(adapter.upsertOne(state, payload.data), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.data.offerId] }),
      ),
    assign: (
      state,
      payload: {
        offerId: number;
        requisitionId: number;
        copyData: boolean;
        fillOpenings?: OpeningAssignment[];
      },
    ) => adapter.markAsPending(state, { scope: null, entities: [payload.offerId] }),
    assignSuccess: (
      state,
      payload: { offerId: number; data: JobRequisition; copyData: boolean },
    ) => {
      const { offerId, data } = payload;
      const entity = {
        offerId: state.entityMap[offerId]!.offerId,
        requisitions: [...state.entityMap[offerId]!.requisitions, data],
      };

      return pipeState(
        state,
        next => adapter.upsertOne(next, entity),
        next => adapter.markAsLoaded(next, { entities: [payload.offerId], scope: '' }),
      );
    },
    assignFailed: ngrxEvent<{
      error: HttpErrorResponse;
    }>(),
    assignOpenings: ngrxEvent<{
      offerId: number;
      assignmentsByRequisitionId: { [key: number]: OpeningAssignment[] };
    }>(),
    assignOpeningsSuccess: ngrxEvent<{ offerId: number; filledOpeningsCount: number }>(),
    assignOpeningsFailed: ngrxEvent<{
      error: HttpErrorResponse;
    }>(),
    unassign: (state, payload: { offerId: number; requisitionId: number }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.offerId] }),
    unassignSuccess: (state, payload: { offerId: number; requisitionId: number }) => {
      const { offerId, requisitionId } = payload;
      const entity = {
        offerId: state.entityMap[offerId]?.offerId!,
        requisitions: [
          ...state.entityMap[offerId]!.requisitions.filter(
            requisition => requisition.id !== requisitionId,
          ),
        ],
      };
      return pipeState(
        state,
        next => adapter.upsertOne(next, entity),
        next => adapter.markAsLoaded(next, { entities: [offerId], scope: '' }),
      );
    },
    unassignFailed: ngrxEvent<{
      error: HttpErrorResponse;
    }>(),
    fetchDiff: ngrxEvent<{ offerId: number; requisitionId: number }>(),
    fetchDiffSuccess: ngrxEvent<CompareFieldData[]>(),
    fetchDiffFailed: ngrxEvent<{
      error: HttpErrorResponse;
    }>(),
    copyData: (state, payload: { offerId: number; requisitionId: number }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.offerId] }),
    copyDataSuccess: (state, payload: { data: { offer: OfferFull } }) =>
      adapter.markAsLoaded(state, { scope: null, entities: [payload.data.offer.id] }),
    copyDataFailed: (state, payload: { error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error),
  }),
});

const selectors = {
  byOfferId: memoizeSelector((id: number) =>
    createSelector(
      defaultSelectors.byId(id),
      (state: JobRequisitionsEntity | null) => state?.requisitions || null,
    ),
  ),
  approved: memoizeSelector((id: number) =>
    createSelector(
      defaultSelectors.byId(id),
      state => state?.requisitions.filter(req => req.status === RequisitionStatus.Approved) || null,
    ),
  ),
  meta: defaultSelectors.meta,
};

const actions = {
  fetchOne: defaultActions.fetchOne,
  fetchOneSuccess: defaultActions.fetchOneSuccess,
  fetchOneFailed: defaultActions.fetchOneFailed,
  assign: defaultActions.assign,
  assignSuccess: defaultActions.assignSuccess,
  assignFailed: defaultActions.assignFailed,
  assignOpenings: defaultActions.assignOpenings,
  assignOpeningsSuccess: defaultActions.assignOpeningsSuccess,
  assignOpeningsFailed: defaultActions.assignOpeningsFailed,
  unassign: defaultActions.unassign,
  unassignSuccess: defaultActions.unassignSuccess,
  unassignFailed: defaultActions.unassignFailed,
  fetchDiff: defaultActions.fetchDiff,
  fetchDiffSuccess: defaultActions.fetchDiffSuccess,
  fetchDiffFailed: defaultActions.fetchDiffFailed,
  copyData: defaultActions.copyData,
  copyDataSuccess: defaultActions.copyDataSuccess,
  copyDataFailed: defaultActions.copyDataFailed,
};

export {
  actions as JobRequisitionsActions,
  namespace as JobRequisitionsNamespace,
  selectors as JobRequisitionsSelectors,
  reducer as JobRequisitionsReducer,
};
